import { apiFetch } from '@lib/apiFetch';
import { CHAT_AGENT } from '@services/agent/chatAgents';
import type { components } from '@type/api/apiSchema';

/**
 * Every session endpoint here is told which agent to run on, explicitly.
 *
 * Omitting `agent` makes the backend fall back to the legacy `bestie` agent — that default exists so
 * already-shipped mobile builds keep working, and it is deliberately not what this client wants. The web
 * app can be released alongside the backend, so it always names the agent.
 *
 * The generated schema publishes `agent?: 'bestie' | 'bestie-client' | 'one-bestie'` on all three
 * endpoints used here, so this is type-checked against the real contract — a typo or a retired agent
 * name fails the build rather than 400-ing at runtime.
 */
const agentQueryParam = { agent: CHAT_AGENT } as const;

/** Camel-case shape returned by all three session wrappers */
export interface VoiceSession {
  wssUrl: string;
  threadId: string;
  expiresIn: number | null;
  /**
   * null means the session sends no prompt override at all, so the agent runs on its
   * own definition default prompt. Any object here — even an empty one — is an override.
   */
  prompt: { parts: string[]; variables?: Record<string, unknown> } | null;
  config: Record<string, unknown> | null;
  extractionSchema: Record<string, unknown> | undefined;
  /** Agent's opening message for chat sessions; null for voice-websocket. */
  firstMessage: string | null;
}

type RawPayload = components['schemas']['ResumeInterviewSessionPayload'];

interface RawPrompt {
  parts?: string[];
  variables?: Record<string, unknown>;
}

function mapPayload(raw: RawPayload): VoiceSession {
  const rawPrompt = raw.prompt ? (raw.prompt as unknown as RawPrompt) : undefined;
  return {
    wssUrl: raw.wss_url ?? '',
    threadId: raw.thread_id ?? '',
    expiresIn: raw.expires_in ?? null,
    // An empty `parts` list is the backend's marker for "no prompt override at all", mapped
    // to null here so callers have a single check. It is not sent as a null prompt on the
    // wire: widening that response property to nullable would be a breaking change for every
    // endpoint sharing this payload. Personas that do have a prompt always yield a non-empty
    // list, so an empty one is unambiguous.
    prompt: rawPrompt?.parts?.length
      ? { parts: rawPrompt.parts, variables: rawPrompt.variables }
      : null,
    config: raw.config ? raw.config : null,
    extractionSchema: raw.extraction_schema ?? undefined,
    firstMessage: raw.first_message ?? null,
  };
}

/**
 * POST /v1/voice/bestie-session?agent=one-bestie
 * Returns a pre-built voice session payload for the generic Bestie voice modal.
 */
export async function fetchBestieVoiceSession(locale: Locale = 'ro'): Promise<VoiceSession> {
  const { data, error } = await apiFetch('/v1/voice/bestie-session', {
    method: 'post',
    locale,
    isClient: true,
    queryParams: agentQueryParam,
  });
  if (error || !data) {
    throw new Error(`fetchBestieVoiceSession failed: ${error ? JSON.stringify(error) : 'no data'}`);
  }
  return mapPayload(data);
}

/**
 * POST /v1/voice/bestie-session?agent=one-bestie — employer-facing entry point.
 *
 * Sends the same agent as the candidate call. Which persona the session runs as is resolved
 * server-side from the loaded context; this client no longer names it. (It previously sent
 * `agent=bestie-client`, back when that parameter selected the persona rather than the agent.)
 */
export async function fetchBestieClientVoiceSession(locale: Locale = 'ro'): Promise<VoiceSession> {
  const { data, error } = await apiFetch('/v1/voice/bestie-session', {
    method: 'post',
    locale,
    isClient: true,
    queryParams: agentQueryParam,
  });
  if (error || !data) {
    throw new Error(`fetchBestieClientVoiceSession failed: ${error ? JSON.stringify(error) : 'no data'}`);
  }
  return mapPayload(data);
}

/**
 * Interview variant:
 *  - 'profile': general profile-building interview (candidate_profile_interview)
 *  - 'answer':  shout-back — answering a specific story-card question (candidate_answer_interview)
 */
export type ResumeInterviewKind = 'profile' | 'answer';

/**
 * In-flight session requests, keyed by request identity. Each session invoke creates a fresh
 * backend thread + ConversationSession, so duplicate concurrent calls for the same request —
 * React StrictMode double-mounts, modal transition remounts — must collapse into ONE network
 * call. The entry is cleared once settled, so a genuine later re-open still starts a new session.
 */
const inFlightSessions = new Map<string, Promise<VoiceSession>>();

/**
 * POST /v1/resume/interview/session or /answer-session?agent=one-bestie { questions, channel }
 * Returns a pre-built session payload for resume interview (voice-websocket or chat).
 * `kind` selects the persona: 'profile' (default) vs 'answer' (shout-back).
 * Concurrent identical calls share a single request (see inFlightSessions).
 */
export async function startResumeInterviewSession(
  questions: string[],
  channel: 'voice-websocket' | 'chat' | 'voice-phone' | 'whatsapp',
  locale: Locale = 'ro',
  firstMessage?: string,
  kind: ResumeInterviewKind = 'profile',
): Promise<VoiceSession> {
  const key = JSON.stringify([kind, channel, locale, firstMessage ?? null, questions]);
  const inFlight = inFlightSessions.get(key);
  if (inFlight) {
    return inFlight;
  }

  // apiFetch stringifies the payload but does not set Content-Type; without it the backend's
  // MapRequestPayload can't detect JSON and 500s ("Unsupported format: txt").
  // first_message is the agent's opening line; the backend sets it on the session config.
  const config = {
    method: 'post' as const,
    locale,
    isClient: true,
    headers: { 'Content-Type': 'application/json' },
    queryParams: agentQueryParam,
    payload: { questions, channel, ...(firstMessage ? { first_message: firstMessage } : {}) },
  };

  const request = (async (): Promise<VoiceSession> => {
    // apiFetch is generic on the literal path, so the endpoint must be a literal per branch.
    const { data, error } = kind === 'answer'
      ? await apiFetch('/v1/resume/interview/answer-session', config)
      : await apiFetch('/v1/resume/interview/session', config);
    if (error || !data) {
      throw new Error(`startResumeInterviewSession failed: ${error ? JSON.stringify(error) : 'no data'}`);
    }
    return mapPayload(data);
  })();

  inFlightSessions.set(key, request);
  try {
    return await request;
  } finally {
    inFlightSessions.delete(key);
  }
}
